import { backendClient } from '@/lib/api-client';
import type { PromptCategory, PromptResponse, PromptScope } from '@/components/case-partner/types';

export interface PromptLibraryApi {
  listCategories: () => Promise<PromptCategory[]>;
  createCategory: (name: string, scope: PromptScope) => Promise<PromptCategory>;
  createPrompt: (payload: {
    title: string;
    content: string;
    category_id: string;
  }) => Promise<PromptResponse>;
  updatePrompt: (
    promptGroupId: string,
    payload: { title: string; content: string; category_id: string },
  ) => Promise<PromptResponse>;
  deletePrompt: (promptGroupId: string) => Promise<void>;
  listVersions: (promptGroupId: string) => Promise<PromptResponse[]>;
}

export function createMatterPromptLibraryApi(slug: string): PromptLibraryApi {
  const base = `/prompt-library/${slug}`;
  return {
    listCategories: async () => {
      const response = await backendClient.get(`${base}/categories`);
      return response.data;
    },
    createCategory: async (name, scope) => {
      const response = await backendClient.post(`${base}/categories`, { name, scope });
      return response.data;
    },
    createPrompt: async (payload) => {
      const response = await backendClient.post(base, payload);
      return response.data;
    },
    updatePrompt: async (promptGroupId, payload) => {
      const response = await backendClient.put(`${base}/prompt/${promptGroupId}`, payload);
      return response.data;
    },
    deletePrompt: async (promptGroupId) => {
      await backendClient.delete(`${base}/prompt/${promptGroupId}`);
    },
    listVersions: async (promptGroupId) => {
      const response = await backendClient.get(`${base}/prompt/${promptGroupId}/versions`);
      return response.data;
    },
  };
}

export function createAdminPromptLibraryApi(): PromptLibraryApi {
  const base = '/prompt-library/firm';
  return {
    listCategories: async () => {
      const response = await backendClient.get(`${base}/categories`);
      return response.data;
    },
    createCategory: async (name, _scope) => {
      // Firm API always persists tenant scope; schema still requires a scope field.
      const response = await backendClient.post(`${base}/categories`, {
        name,
        scope: 'tenant',
      });
      return response.data;
    },
    createPrompt: async (payload) => {
      const response = await backendClient.post(base, payload);
      return response.data;
    },
    updatePrompt: async (promptGroupId, payload) => {
      const response = await backendClient.put(`${base}/${promptGroupId}`, payload);
      return response.data;
    },
    deletePrompt: async (promptGroupId) => {
      await backendClient.delete(`${base}/${promptGroupId}`);
    },
    listVersions: async (promptGroupId) => {
      const response = await backendClient.get(`${base}/${promptGroupId}/versions`);
      return response.data;
    },
  };
}

export async function listAdminFirmPrompts(): Promise<PromptResponse[]> {
  const response = await backendClient.get('/prompt-library/firm');
  return response.data;
}
